/**
 * @graphql agg
 */

import gql from 'graphql-tag';

import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql';
import { fg } from '@confluence/feature-gating';

import { MentionsUsersQuery } from './MentionsUsersQuery.graphql';
import type {
	usersV2Bulk as MentionsUsersQueryType,
	usersV2BulkVariables as MentionsUsersQueryVariables,
	usersV2Bulk_usersV2,
} from './__types__/MentionsUsersQuery';
import type {
	preloadMentionsUsersUsersQuery as preloadMentionsUsersUsersQuery$data,
	preloadMentionsUsersUsersQueryVariables as preloadMentionsUsersUsersQuery$variables,
	preloadMentionsUsersUsersQuery_users,
} from './__types__/preloadMentionsUsersUsersQuery';

type UserIdAri = `ari:cloud:identity::user/${string}`;
const ARI_PREFIX = 'ari:cloud:identity::user/';

export const MENTIONS_LOOKUP_LIMIT = 50;

export type MentionUserEntry = {
	id?: string | null;
	name?: string | null;
};

type GetUserDataReturn =
	| (usersV2Bulk_usersV2 | preloadMentionsUsersUsersQuery_users | null)[]
	| null
	| undefined;

const getUserData = async (ids: UserIdAri[]): Promise<GetUserDataReturn> => {
	if (!fg('cc-agg-migration_mentions-users-to-agg')) {
		const { data } = await query<MentionsUsersQueryType, MentionsUsersQueryVariables>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: MentionsUsersQuery,
			variables: {
				ids,
			},
		});
		return data?.usersV2;
	}

	const { data } = await queryAgg<
		preloadMentionsUsersUsersQuery$data,
		preloadMentionsUsersUsersQuery$variables
	>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: gql`
			query preloadMentionsUsersUsersQuery($ids: [ID!]!) {
				users(accountIds: $ids) {
					id
					name
				}
			}
		`,
		variables: {
			ids,
		},
	});

	return data?.users;
};

export const preloadMentionsUsers = async (userIds: string[]): Promise<MentionUserEntry[]> => {
	const ids = userIds.map((id: string) => `${ARI_PREFIX}${id}`) as UserIdAri[];
	const users = await getUserData(ids);

	return (users || []).map((user) => ({
		id: user?.id?.replace(ARI_PREFIX, ''),
		name: user?.name,
	}));
};
